import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { DialogContent } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { CountrySelect } from 'components/ui/country-select';
import { Form, FormField, FormItem, FormControl, FormMessage } from 'components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Check, ChevronsUpDown, CreditCard, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { ACCOUNT_QUERY_KEY, useCreateAccount } from 'features/profile/hooks/use-account';
import { useOrganizations } from 'features/organization/hooks/use-organization';
import { useGetRolesQuery } from 'features/iam/hooks/use-iam';
import { Badge } from 'components/ui/badge';
import { useTranslation } from 'react-i18next';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { ROLES } from 'constant/roles';
import { ORGANIZATION_CONSTANTS } from 'constant/organization';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { SEAT_POOLS_QUERY_KEY, useMoveSeats, usePlanSeats } from 'features/subscription/hooks/use-seats';
import { PlanSeats, quantityForMore, seatsToAdd, seatsToPay } from 'features/subscription/utils/seat-pools';
import { SeatPriceSummary, useSeatPrice } from 'features/iam/components/seats/add-seat-dialog';
import { BillingDetailsFormHandle } from 'features/subscription/components/billing-details-form';
import { availableSeatsLabel } from 'features/iam/components/seats/plan-actions';
import { AddUserHeader } from './add-user-header';
import { AddUserPaymentStep } from './add-user-payment-step';

/**
 * AddUser Component
 *
 * A dialog component that displays a form for creating a new user account. On successful submission,
 * it invalidates account and user list queries so the table refetches without a page reload.
 *
 * Features:
 * - Uses `react-hook-form` for form state and validation
 * - Submits form data via `useCreateAccount` mutation
 * - Auto-refreshes relevant queries on success
 * - Provides input fields for first name, last name, email, and organization selection
 * - Organization autocomplete with search functionality
 * - Styled using Tailwind and ShadCN dialog components
 *
 * Props:
 * - `onClose` (function): Callback to close the dialog
 *
 * @param {AddUserProps} props - Component props
 * @returns {JSX.Element} A modal with a user invitation form
 *
 * @example
 * <AddUser onClose={() => setIsDialogOpen(false)} />
 */

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  country: string;
  salutation: string;
  organizationId: string;
  roles: string[];
  currentRole: string;
}

type AddUserProps = {
  onClose: () => void;
};

const MAX_ROLES = 3;
/** A plan the invite can put someone on: paid for, and with a seat available or one to buy. */
const NO_PLAN = 'no-plan';

const selectable = (entry: PlanSeats) => !entry.awaitingPayment && (entry.freeThisPeriod > 0 || entry.canAddSeats);
const HIDDEN_ROLE_NAMES = new Set(['cloudadmin']);

// Teams "Add user" look (Figma 10827:7912 / 10827:9293 / 10827:9759).
const dialogClassName =
  'max-h-screen w-[calc(100vw-2rem)] gap-0 overflow-y-auto rounded-2xl border-grey-100 p-7 shadow-choice-card sm:max-w-xl dark:border-border';
const labelClassName = 'text-sm font-medium leading-body text-grey-950 dark:text-true-white';
const fieldClassName =
  'h-11 rounded-section border-grey-100 bg-true-white px-3.5 text-sm text-grey-900 shadow-none aria-[invalid=true]:border-error-700 dark:border-border dark:bg-card dark:text-true-white';
const helperClassName = 'text-xs leading-heading text-grey-500 dark:text-grey-400';
const errorClassName = 'text-xs font-normal leading-heading text-error-700';
const footerClassName = 'mt-6 flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center';
const pillClassName = 'h-10 w-full rounded-full text-sm font-medium leading-body shadow-none sm:w-auto';
const outlinePillClassName = cn(
  pillClassName,
  'border-grey-100 bg-true-white px-4 text-grey-900 hover:bg-grey-50 dark:border-border dark:bg-card dark:text-true-white dark:hover:bg-muted'
);
const ghostPillClassName = cn(
  pillClassName,
  'px-4 text-grey-900 hover:bg-grey-50 dark:text-true-white dark:hover:bg-muted'
);
const primaryPillClassName = cn(pillClassName, 'bg-grey-950 px-5 text-true-white hover:bg-grey-900');
const ALLOWED_ADD_USER_ROLE_NAMES: Set<string> = new Set([ROLES.USER, ROLES.ADMIN]);

export const AddUser: React.FC<AddUserProps> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
  const [organizationOpen, setOrganizationOpen] = useState(false);
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const currentUser = useCommonStore((state) => state.user);
  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const [availableRoles, setAvailableRoles] = useState<
    Array<{
      name: string;
      slug: string;
    }>
  >([]);

  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({
    page: 0,
    pageSize: 100,
    filter: { search: '' },
    sort: { property: 'name', isDescending: false },
  });

  // Fetch organizations for autocomplete
  const { data: organizationsData } = useOrganizations({
    page: 0,
    pageSize: 50,
    name: organizationSearchTerm,
    enabled: true,
  });

  const organizations = (organizationsData?.data || []).filter(
    org => org.name !== ORGANIZATION_CONSTANTS.DEFAULT_ORGANIZATION_NAME
  );


  useEffect(() => {
    if (rolesData?.data) {
      const mappedRoles = rolesData.data
        .filter(role => {
          return ALLOWED_ADD_USER_ROLE_NAMES.has(role.name);
        })
        .map((role) => ({
          name: role.name,
          slug: role.name.toLowerCase().replace(/\s+/g, '-'),
        }));
      if (mappedRoles) setAvailableRoles(mappedRoles);
    }
  }, [isOrgAdmin, rolesData]);

  // One step or two depends on whether billing details are saved, so check them fresh on every open.
  // Reset, not invalidate: this runs before the queries below subscribe, and they skip refetching on
  // mount, so only dropping the cached data makes them fetch again.
  useEffect(() => {
    queryClient.resetQueries({ queryKey: ['subscription-billing-profile'] });
    queryClient.resetQueries({ queryKey: [SEAT_POOLS_QUERY_KEY, 'quote'] });
  }, [queryClient]);

  const { mutateAsync: createAccount, isPending: isCreating } = useCreateAccount();
  const { planSeats } = usePlanSeats();
  const { mutateAsync: moveSeats, isPending: isSeating } = useMoveSeats();
  const [planChoice, setPlanChoice] = useState<string>('');
  // A plan is optional. It starts on a plan with a free seat, and never on one that buys a seat.
  const defaultPlan = planSeats.find((entry) => selectable(entry) && entry.freeThisPeriod > 0)?.code ?? NO_PLAN;
  const chosenCode = planChoice || defaultPlan;
  const chosenPlan = planSeats.find((entry) => entry.code === chosenCode);
  const addSeats = chosenPlan ? seatsToAdd(chosenPlan, 1) : 0;
  const buysSeat = !!chosenPlan && seatsToPay(chosenPlan, 1) > 0;
  const targetQuantity = chosenPlan ? quantityForMore(chosenPlan, addSeats) : 0;
  const oneMoreSeat = useSeatPrice(buysSeat ? chosenPlan : null, buysSeat ? targetQuantity : null);
  // An invite that buys a seat while the billing details are missing takes two steps: the person
  // and plan, then the billing details and payment. With details saved it stays one step.
  const [step, setStep] = useState<1 | 2>(1);
  // Once on step 2, stay there even after saving the details stops the price asking for them.
  const isTwoStep = buysSeat && (oneMoreSeat.needsBilling || step === 2);
  const isPaymentStep = isTwoStep && step === 2;
  const billingRef = useRef<BillingDetailsFormHandle>(null);
  // Missing billing details are the only thing in the way, so Pay and send invite can save them first.
  const canPayOnPaymentStep =
    oneMoreSeat.ready ||
    (oneMoreSeat.needsBilling && !oneMoreSeat.problem && oneMoreSeat.blockers.length === 0);

  const form = useForm<FormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      country: '',
      salutation: '',
      organizationId: '',
      roles: [],
      currentRole: '',
    },
  });

  const { control, handleSubmit, setValue, watch } = form;
  // Step 2 also waits on saving billing details, which no mutation here tracks.
  const isSubmitting = isCreating || isSeating || form.formState.isSubmitting;
  const selectedOrganizationId = watch('organizationId');
  const selectedOrganization = organizations.find(org => org.itemId === selectedOrganizationId);
  const watchedValues = watch();
  const isOrgAdminOrUser = watchedValues.roles?.includes(ROLES.ADMIN) || watchedValues.roles?.includes(ROLES.USER);
  const currentMembershipOrganizationId =
    selectedOrgId ||
    currentUser?.organizationId ||
    'default';


  const onSubmit = async (data: FormData) => {
    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      country: data.country,
      userPassType: 1,
      userCreationType: 1,
      platform: 'blocks_portal',
      organizationId: currentMembershipOrganizationId,
      organizationIds: isAdmin ? (data.organizationId == "" ? [] : [data.organizationId]) : organizationsData?.data?.map((org) => org.itemId),
      roles: data.roles ?? [],
      permissions: [],
      // memberships: [
      //   {
      //     organizationId: currentMembershipOrganizationId,
      //     roles: data.roles ?? [],
      //   }
      // ],
    };

    // Two-step invite: save the billing details first, and stop here if they could not be saved.
    if (isPaymentStep && oneMoreSeat.needsBilling) {
      const isSaved = await billingRef.current?.save();
      if (!isSaved) return;
    }

    const created = await createAccount(payload);
    if (chosenPlan && created?.itemId) {
      // The person is invited either way; a seat that fails is shown by the seat hook and can be
      // given later from the Plan column.
      await moveSeats({ people: [{ userId: created.itemId }], to: chosenPlan, add: addSeats }).catch(
        () => undefined
      );
    }

    await queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: ['getUsers'] });
    onClose();
  };

  const handleAddRole = (roleSlug: string) => {
    if (!roleSlug) return;

    const currentRoles = form.getValues('roles') || [];

    if (currentRoles.length >= MAX_ROLES) return;

    if (!currentRoles.includes(roleSlug)) {
      setValue('roles', [...currentRoles, roleSlug]);
    }
    setValue('currentRole', '');
  };

  const handleRemoveRole = (roleToRemove: string) => {
    const currentRoles = form.getValues('roles') || [];
    setValue(
      'roles',
      currentRoles.filter((role) => role !== roleToRemove)
    );
  };

  const getAvailableRoles = () => {
    const selectedRoles = form.getValues('roles') || [];
    return availableRoles.filter((role) => !selectedRoles.includes(role.slug));
  };

  /* const getRoleNameBySlug = (slug: string) => {
    const role = availableRoles.find((r) => r.slug === slug);
    return role ? role.name : slug;
  }; */

  const isMaxRolesReached = () => {
    const selectedRoles = form.getValues('roles') || [];
    return selectedRoles.length >= MAX_ROLES;
  };

  const handleContinue = async () => {
    const isValid = await form.trigger();
    if (isValid) setStep(2);
  };

  const getRolePlaceholder = () => {
    if (isLoadingRoles) return `${t('LOADING_ROLES')}...`;
    if (isMaxRolesReached()) return t('MAX_ROLES_REACHED');
    if (watchedValues.roles.length > 0) return t('ADD_ROLE', 'Add role');
    return t('SELECT_ROLES');
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };


  if (isPaymentStep && chosenPlan) {
    return (
      <DialogContent className={dialogClassName} onClick={handleDialogClick} hideClose>
        <AddUserHeader
          title={t('REVIEW_AND_PAY', 'Review and pay')}
          description={t('REVIEW_AND_PAY_DESCRIPTION', 'Check the seat price, then pay to send the invitation.')}
          step={2}
          onClose={onClose}
        />
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
            <AddUserPaymentStep
              name={`${watchedValues.firstName} ${watchedValues.lastName}`}
              email={watchedValues.email}
              plan={chosenPlan}
              quantity={targetQuantity}
              price={oneMoreSeat}
              billingRef={billingRef}
            />
            <div className={cn(footerClassName, 'sm:justify-between')}>
              <Button
                variant="outline"
                type="button"
                onClick={() => setStep(1)}
                disabled={isSubmitting}
                className={outlinePillClassName}
              >
                {t('BACK', 'Back')}
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || !canPayOnPaymentStep}
                className={primaryPillClassName}
              >
                {oneMoreSeat.dueNow
                  ? t('PAY_AND_INVITE', { amount: oneMoreSeat.dueNow, defaultValue: 'Pay {{amount}} and invite' })
                  : t('INVITE_USER')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    );
  }

  return (
    <DialogContent className={dialogClassName} onClick={handleDialogClick} hideClose>
      <AddUserHeader
        title={t('ADD_USER')}
        description={t('ADD_USER_DESCRIPTION')}
        step={isTwoStep ? 1 : null}
        onClose={onClose}
      />
      <Form {...form}>
        {/* noValidate: the email rules below show the errors, not the browser's own popup. */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col" noValidate>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={control}
                name="firstName"
                rules={{
                  required: t('FIRST_NAME_REQUIRED'),
                  maxLength: {
                    value: 15,
                    message: t('MAX_15_CHARACTERS')
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <Label className={labelClassName}>{t('FIRST_NAME')}*</Label>
                    <FormControl>
                      <Input {...field} placeholder={t('ENTER_YOUR_FIRST_NAME')} className={fieldClassName} />
                    </FormControl>
                    <FormMessage className={errorClassName} />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="lastName"
                rules={{
                  required: t('LAST_NAME_REQUIRED'),
                  maxLength: {
                    value: 15,
                    message: t('MAX_15_CHARACTERS')
                  }
                }}
                render={({ field }) => (
                  <FormItem>
                    <Label className={labelClassName}>{t('LAST_NAME')}*</Label>
                    <FormControl>
                      <Input {...field} placeholder={t('ENTER_YOUR_LAST_NAME')} className={fieldClassName} />
                    </FormControl>
                    <FormMessage className={errorClassName} />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={control}
              name="email"
              rules={{ required: t('EMAIL_REQUIRED'), pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('ENTER_VALID_EMAIL') } }}
              render={({ field }) => (
                <FormItem>
                  <Label className={labelClassName}>{t('EMAIL')}*</Label>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder={t('ENTER_YOUR_EMAIL_ADDRESS')}
                      className={fieldClassName}
                    />
                  </FormControl>
                  <FormMessage className={errorClassName} />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="currentRole"
              rules={{ required: watchedValues.roles.length > 0 ? false : t('ROLES_REQUIRED') }}
              render={({ field, fieldState }) => (
                <FormItem>
                  <Label className={labelClassName}>
                    {t('ROLES')}{isOrgAdmin ? "*" : ""}
                  </Label>
                  {/* Chosen roles sit inside the field, next to the picker for the next one. */}
                  <div
                    className={cn(
                      'flex min-h-11 w-full flex-wrap items-center gap-2 rounded-section border border-grey-100 bg-true-white px-2 py-1.5 focus-within:ring-1 focus-within:ring-inset focus-within:ring-ring dark:border-border dark:bg-card',
                      fieldState.error && 'border-error-700'
                    )}
                  >
                    {watchedValues.roles?.map((roleSlug) => (
                      <Badge
                        key={roleSlug}
                        variant="secondary"
                        className="flex items-center gap-1 rounded-card border-0 bg-grey-100 pr-1 font-medium text-grey-900 hover:bg-grey-100 dark:bg-muted dark:text-true-white dark:hover:bg-muted"
                      >
                        <span>{t(roleSlug?.toUpperCase())}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          aria-label={`${t('REMOVE', 'Remove')} ${t(roleSlug?.toUpperCase())}`}
                          className="size-4 p-0 text-grey-600 hover:bg-transparent hover:text-grey-950 dark:text-grey-400 dark:hover:bg-transparent dark:hover:text-true-white"
                          onClick={() => handleRemoveRole(roleSlug)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                    {(isLoadingRoles || getAvailableRoles().length > 0) && (
                    <Select
                      onValueChange={(value) => {
                        if (value === ROLES.ADMIN && isAdmin) {
                          setValue('organizationId', '');
                        }
                        handleAddRole(value);
                      }}
                      value={field.value}
                      disabled={isLoadingRoles || isMaxRolesReached()}
                    >
                      <FormControl>
                        <SelectTrigger className="h-8 min-w-32 flex-1 border-0 bg-transparent px-1.5 text-sm text-grey-900 shadow-none focus:ring-0 focus-visible:ring-0 dark:text-true-white">
                          <SelectValue placeholder={getRolePlaceholder()} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {getAvailableRoles().map((role) => (
                          <SelectItem key={role.slug} value={role.slug}>
                            <span>{t(role.slug?.toUpperCase())}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    )}
                  </div>
                  <FormMessage className={errorClassName} />
                </FormItem>
              )}
            />

            {/* Same spacing as a FormItem, so this field lines up with the ones above. */}
            <div className="space-y-2">
              <Label htmlFor="invite-plan" className={labelClassName}>
                {t('SELECT_A_PLAN_OPTIONAL', 'Plan (optional)')}
              </Label>
              <Select value={chosenCode} onValueChange={setPlanChoice}>
                <SelectTrigger id="invite-plan" className={fieldClassName}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PLAN}>{t('INVITE_WITHOUT_PLAN', 'No plan')}</SelectItem>
                  {planSeats.map((entry) => (
                    <SelectItem
                      key={entry.code}
                      value={entry.code}
                      disabled={!selectable(entry)}
                    >
                      {entry.name} · {availableSeatsLabel(t, entry.freeThisPeriod)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!chosenPlan && (
                <p className={helperClassName}>
                  {t('INVITE_WITHOUT_PLAN_HINT', 'No seat is bought. They use your organization credits.')}
                </p>
              )}
              {chosenPlan && !buysSeat && (
                <p className={helperClassName}>
                  {t('USES_AVAILABLE_SEAT', { plan: chosenPlan.name, defaultValue: 'Uses an available {{plan}} seat. Nothing to pay.' })}
                </p>
              )}
              {chosenPlan && buysSeat && !isTwoStep && (
                <>
                  <p className={helperClassName}>
                    {t('INVITE_BUYS_SEAT', {
                      plan: chosenPlan.name,
                      defaultValue: 'No {{plan}} seats available. Inviting buys one:',
                    })}
                  </p>
                  <SeatPriceSummary entry={chosenPlan} quantity={targetQuantity} />
                </>
              )}
              {chosenPlan && isTwoStep && (
                <p className={helperClassName}>
                  {t('INVITE_ADDS_SEAT', {
                    plan: chosenPlan.name,
                    defaultValue: "No {{plan}} seats left. Inviting adds 1 seat. You'll see the price and pay on the next step.",
                  })}
                </p>
              )}
              {/* {chosenPlan && buysSeat && oneMoreSeat.needsBilling && (
                <div className="flex items-start gap-3 rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
                  <CreditCard className="mt-0.5 h-4 w-4 shrink-0" /> */}
                  {/* <span>
                    {t(
                      'SEAT_NEEDS_BILLING_NEXT_STEP',
                      "Adding a seat needs billing details. You'll add them on the next step, one time only."
                    )}
                  </span> */}
                {/* </div>
              )} */}
            </div>
          </div>
          <div className={cn(footerClassName, 'sm:justify-end')}>
            <Button variant="ghost" type="button" onClick={onClose} className={ghostPillClassName}>
              {t('CANCEL')}
            </Button>
            {isTwoStep ? (
              <Button
                type="button"
                onClick={handleContinue}
                disabled={!chosenPlan}
                className={primaryPillClassName}
              >
                {t('CONTINUE_TO_BILLING', 'Continue to billing')}
              </Button>
            ) : (
              <Button
                type="submit"
                loading={isCreating || isSeating}
                disabled={isCreating || isSeating || (buysSeat && !oneMoreSeat.ready)}
                className={primaryPillClassName}
              >
                {buysSeat && oneMoreSeat.dueNow
                  ? t('PAY_AND_INVITE', { amount: oneMoreSeat.dueNow, defaultValue: 'Pay {{amount}} and invite' })
                  : t('INVITE_USER')}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </DialogContent>
  );
};
